import { h } from "preact";
import { useRef, useEffect } from "preact/hooks";
import PropTypes from "prop-types";

const Progress = ({ percentage, onClick }) => {
  const progressRef = useRef();
  const progressFrontRef = useRef();
  const progressCircleRef = useRef();
  const isDragging = useRef(false);

  const getSeekPercentage = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    if (e.changedTouches?.[0]?.clientX) {
      const x = e.changedTouches[0].clientX - rect.left;
      return ((100 / rect.width) * x).toFixed(3);
    }
    const x = e.clientX - rect.left;
    return ((100 / rect.width) * x).toFixed(3);
  };

  const updateUI = (p) => {
    progressFrontRef.current.style.width = `${p}%`;
    progressCircleRef.current.style.left = `${p - 4}%`;
  };

  const handleDragging = (e, triggerDragChange) => {
    let seekPercentage = parseFloat(getSeekPercentage(e));
    if (seekPercentage < 0) {
      seekPercentage = 0;
    }
    if (seekPercentage > 100) {
      seekPercentage = 100;
    }
    updateUI(seekPercentage);
    if (triggerDragChange) {
      onClick(seekPercentage);
    }
  };

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  useEffect(() => {
    const mouseUpHandler = (e) => {
      if (isDragging.current) {
        isDragging.current = false;
        handleDragging(e, true);
      }
    };
    const mouseMoveHandler = (e) => {
      if (isDragging.current) {
        handleDragging(e);
      }
    };
    document.addEventListener("mouseup", mouseUpHandler);
    document.addEventListener("touchend", mouseUpHandler);
    document.addEventListener("mousemove", mouseMoveHandler);
    document.addEventListener("touchmove", mouseMoveHandler);
    return () => {
      document.removeEventListener("mouseup", mouseUpHandler);
      document.removeEventListener("touchend", mouseUpHandler);
      document.removeEventListener("mousemove", mouseMoveHandler);
      document.removeEventListener("touchmove", mouseMoveHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isDragging.current) {
      updateUI(percentage);
    }
  }, [percentage]);

  return (
    <div
      ref={progressRef}
      className="c-progress"
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      role="progressbar"
      aria-valuenow={percentage}
      aria-valuemin="0"
      aria-valuemax="100"
      aria-label="Progress Bar"
    >
      <div className="c-progress__back" />
      <div className="c-progress__front" ref={progressFrontRef} />
      <div className="c-progress__circle" ref={progressCircleRef} />
    </div>
  );
};

Progress.propTypes = {
  percentage: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default Progress;
